import { ChevronDown, Mic, MicOff, Settings, Video, VideoOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";

type CallSettingsProps = {
};

const CallSettings: React.FC<CallSettingsProps> = () => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { updateMediaSettings } = useAuth();

    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
    const [showSettings, setShowSettings] = useState(false);
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [stream, setStream] = useState<MediaStream | null>(null);

    const getDevices = useCallback(async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            setDevices(deviceList);

            const videoInput = deviceList.find(d => d.kind === 'videoinput');
            const audioInput = deviceList.find(d => d.kind === 'audioinput');

            if (videoInput) setSelectedVideoDevice(videoInput.deviceId);
            if (audioInput) setSelectedAudioDevice(audioInput.deviceId);

        } catch (error) {
            console.error('Erro ao obter permissão ou dispositivos:', error);
        }
    }, []);

    useEffect(() => {
        getDevices();
    }, [getDevices]);

    const updateStream = useCallback(async () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        try {
            const constraints: MediaStreamConstraints = {
                video: { deviceId: { exact: selectedVideoDevice } },
                audio: { deviceId: { exact: selectedAudioDevice } }
            };
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            console.error('Erro ao acessar a câmera:', error);
        }
    }, [selectedVideoDevice, selectedAudioDevice, stream]);

    useEffect(() => {
        if (selectedVideoDevice && selectedAudioDevice) {
            updateStream();
        }

        return () => {
            stream?.getTracks().forEach(track => track.stop());
        };
    }, [selectedVideoDevice, selectedAudioDevice]);

    const toggleVideo = () => {
        if (!stream) {
            return;
        }

        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];
        if (videoTrack) {
            videoTrack.enabled = !videoTrack.enabled;
            setIsVideoEnabled(videoTrack.enabled);
            updateMediaSettings({ 
                video: videoTrack.enabled, 
                audio: audioTrack?.enabled ?? true 
            });
        }
    };

    const toggleAudio = () => {
        if (!stream) {
            return;
        }

        const audioTrack = stream.getAudioTracks()[0];
        const videoTrack = stream.getVideoTracks()[0];
        if (audioTrack) {
            audioTrack.enabled = !audioTrack.enabled;
            setIsAudioEnabled(audioTrack.enabled);
            updateMediaSettings({ 
                video: videoTrack?.enabled ?? true, 
                audio: audioTrack.enabled 
            });
        }
    };

    return (
        <div className="flex flex-col lg:flex-row w-full gap-3 p-6 items-start justify-center">
            <div className={`w-full ${showSettings ? 'lg:w-1/2' : 'lg:w-1/2'}`}>
                <div className="relative bg-black rounded-2xl overflow-hidden mb-6 mx-auto max-w-sm lg:max-w-none" style={{ aspectRatio: '16/10' }}>
                    <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover transition-opacity duration-300 ${!isVideoEnabled ? 'opacity-0' : 'opacity-100'}`} />
                    {!isVideoEnabled && <div className="absolute inset-0 bg-gray-800 flex items-center justify-center"><VideoOff className="w-12 h-12 text-gray-400" /></div>}
                </div>
                <div className="flex justify-center gap-4 mb-8">
                    <button
                        onClick={toggleVideo} 
                        className={`p-4 rounded-full cursor-pointer transition-colors shadow-lg ${isVideoEnabled ? 'bg-white hover:bg-gray-100' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                            <Video />
                    </button>
                    <button
                        onClick={toggleAudio}
                        className={`p-4 rounded-full cursor-pointer transition-colors shadow-lg ${isAudioEnabled ? 'bg-white hover:bg-gray-100' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                            {isAudioEnabled ? <Mic /> : <MicOff />}
                    </button>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-4 rounded-full transition-colors shadow-lg cursor-pointer ${showSettings ? 'bg-indigo-500 text-white' : 'bg-white hover:bg-gray-100'}`}>
                            <Settings />
                    </button>
                </div>
            </div>

            {showSettings && (
                <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm space-y-4 w-1/2 h-auto">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Câmera</label>
                        <div className="relative">
                            <select
                                value={selectedVideoDevice}
                                onChange={(e) => setSelectedVideoDevice(e.target.value)}
                                className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-white appearance-none pr-8"
                            >
                                {devices.filter(d => d.kind === 'videoinput').map(d => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Câmera ${d.deviceId.slice(0, 8)}`}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Microfone</label>
                        <div className="relative">
                            <select
                                value={selectedAudioDevice}
                                onChange={(e) => setSelectedAudioDevice(e.target.value)}
                                className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-white appearance-none pr-8"
                            >
                                {devices.filter(d => d.kind === 'audioinput').map(d => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Microfone ${d.deviceId.slice(0, 8)}`}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CallSettings;